import { Injectable } from '@angular/core';
import { Observable, tap, map, filter, switchMap, catchError, of } from 'rxjs';
import { DsrPrivacyRightsQuery } from '../state/dsr-privacy-rights.query';
import { ApiClientDsrRightsService } from 'src/app/api/dsr/api-client-dsr-rights.service';
import { BrandRef, NewPrivacyRightData, PrivacyRightDeepItem, PrivacyRightExtendedResponse, PrivacyRightItem } from 'src/app/api/models/dsr/dsr-rights.interface';
import { LoggerService } from 'src/app/logger/logger.service';
import { PrivacyRight } from 'src/app/company-settings/configuration/configuration.interface';
import { PredefinedRightIds, RightProcessingTypeEnum } from 'src/app/api/models/dsr/dsr-rights.enum';
import { RightsInPcDialogType } from '../dsr-privacy-rights.enum';
import { DsrMultiBrandQuery } from '../../dsr-multi-brand/state/dsr-multi-brand.query';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { DialogsManagerService } from 'src/app/services/dialogs-manager.service';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialog } from 'src/app/shared/confirmation-dialog/confirmation-dialog.interface';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { escapeHtml } from 'src/app/shared/utils/html.utils';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class DsrPrivacyRightsService {
    private readonly loggerName = 'DsrPrivacyRightsService';
    private predefinedRightIdsSet = new Set<string>(Object.values(PredefinedRightIds));

    constructor(
        private loggerService: LoggerService,
        private apiClientDsrRightsService: ApiClientDsrRightsService,
        private dsrRightsQuery: DsrPrivacyRightsQuery,
        private dsrMultiBrandQuery: DsrMultiBrandQuery,
        private contentPipe: ContentPipe,
        private dialogsManagerService: DialogsManagerService,
    ) { }

    initRights(): Observable<void> {
        return this.apiClientDsrRightsService.getPrivacyRightsShallow().pipe(
            tap(rights => this.dsrRightsQuery.setRights(rights.privacyRights)),
            tap(() => this.loggerService.info(this.loggerName, 'get rights data')),
            map(() => void 0),
            catchError(error => this.onInitRightsError(error)),
            // loading must flip to false on error too - consumers filter on !loading and would wait forever
            tap(() => this.dsrRightsQuery.setLoading(false)));
    }

    // fall back to empty rights on fetch failure so pages gated on the rights loading state don't hang
    private onInitRightsError(error: Error): Observable<void> {
        this.loggerService.error(this.loggerName, `initRights() Error: ${error.name} ,${error.message}`);
        this.dsrRightsQuery.setRights([]);
        return of(void 0);
    }

    hasPrivacyRights(): Observable<boolean> {
        return this.dsrRightsQuery.selectLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.dsrRightsQuery.selectRights()),
            map(rightsMap => rightsMap?.size > 0)
        );
    }

    enablePrivacyRight(id: string, right: Partial<PrivacyRightDeepItem>): Observable<void> {
        return this.apiClientDsrRightsService.updatePrivacyRightDisplaySettings(id, right).pipe(
            tap(() => this.dsrRightsQuery.setRightEnabled(id, right.enabled))
        );
    }

    getPrivacyRightsMap(): Observable<Map<string, PrivacyRight[]>> {
        return this.getPrivacyRightsExtended().pipe(
            map(res => this.createPrivacyRightsMap(res)),
        );
    }

    private getPrivacyRightsExtended(): Observable<PrivacyRightExtendedResponse> {
        return this.apiClientDsrRightsService.getPrivacyRightsExtended();
    }

    createPrivacyRightsMap(response: PrivacyRightExtendedResponse): Map<string, PrivacyRight[]> {
        const map = new Map<string, PrivacyRight[]>();
    
        response.privacyRights
        .filter(privacyRight => !!privacyRight.enabled)
        .forEach(privacyRight => {
            privacyRight.eligibleRegions.forEach((region) => {
                let key = region.continent;
                if (region.country) {
                    key += `:${region.country}`;
                }
                if (region.state) {
                    key += `:${region.state}`;
                }
    
                if (!map.has(key)) {
                    map.set(key, []);
                }

                const transformedRight = this.getTransformedRight(privacyRight);
    
                map.get(key)?.push(transformedRight);
            });
        });
    
        return map;
    }

    getEnabledPrivacyRights(): PrivacyRight[] {
        const rightsMap = this.dsrRightsQuery.getRights()();
        return Array.from(rightsMap.values()).filter(right => right.enabled).map(right => this.getTransformedRight(right as PrivacyRightDeepItem));
    }

    private getTransformedRight(privacyRight: PrivacyRightDeepItem): PrivacyRight {
        return {
			formCtrlName: privacyRight.id,
			title: privacyRight.displayName,
			subtitle: privacyRight.description,
			iconId: this.predefinedRightIdsSet.has(privacyRight.id) ? `icon-${privacyRight.id}-request` : `icon-donotmail-request`, // icon for custom rights
		};
    }

    updateRightNameAndDescription(id: string, name: string, description: string): void {
        this.dsrRightsQuery.updateRightNameAndDescription(id, name, description);
    }

    createNewRight(formData: NewPrivacyRightData): Observable<PrivacyRightItem> {
        return this.apiClientDsrRightsService.createNewPrivacyRight(formData).pipe(
            tap(right => this.dsrRightsQuery.addRight(right)),
        );
    }

    getRightProcessingType(rightId: string): RightProcessingTypeEnum {
        return this.dsrRightsQuery.getRights()().get(rightId)?.rightProcessingType;
    }

    getBrandsFromIds(brandIds: string[]): BrandRef[] {
        const allBrands = this.dsrMultiBrandQuery.getBrands()();
        return brandIds.map(id => ({ id, name: allBrands.find(b => b.id === id)?.name ?? id }));
    }

    openRightsInPcDialog(brands: BrandRef[], type: RightsInPcDialogType): void {
        const bodyKey = type === RightsInPcDialogType.Disable ? 'dsr-setup.rightsInPcDisableBody' : 'dsr-setup.rightsInPcDeleteBody';
        const brandsHtml = `<ul style="text-align:left;margin-top:1.6rem;padding-left:2.4rem">${brands.map(b => `<li><a href="/${RoutesManager.privacyCenter}/${b.id}/${RoutesManager.dynamicPrivacyCenterPrivacyRights}" target="_blank" rel="noopener noreferrer" style="color:inherit">${escapeHtml(b.name)}</a></li>`).join('')}</ul>`;
        const dialogData: ConfirmationDialog = {
            ...this.contentPipe.transform('dsr-setup.rightsInPcTitle'),
            image: `${environment.assets.url}/Portal/warning.png`,
            imageWidth: 24,
            okBtn: this.contentPipe.transform('dsr-setup.rightsInPcOkBtn'),
            text: `${this.contentPipe.transform(bodyKey)}${brandsHtml}`,
        };
        this.dialogsManagerService.openDialogCommon(ConfirmationDialogComponent, dialogData, '475px');
    }
}